import React, { useRef } from 'react';
import { Plus } from 'lucide-react';
import { useAskSwirlStore } from '../../stores/askSwirlStore';
import { useAuth } from '../../contexts/AuthContext';
import { uploadFiles, validateFile } from '../../services/upload.service';
import topicService from '../../services/topicService';

/**
 * Attachment Menu - Direct file upload button
 * Opens desktop file picker directly, no modal
 */
export default function AttachmentMenu() {
  const { addChip, chips } = useAskSwirlStore();
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    console.log('🔵 [AttachmentMenu] handleFileSelect called', {
      hasFiles: !!e.target.files,
      filesCount: e.target.files?.length || 0,
      userId: user?.id,
    });
    
    const files = Array.from(e.target.files);
    console.log('🔵 [AttachmentMenu] Files array:', files.map(f => ({ name: f.name, size: f.size, type: f.type })));
    
    if (files.length === 0) {
      console.warn('🔵 [AttachmentMenu] No files selected, returning early');
      return;
    }

    if (!user?.id) {
      console.error('❌ [AttachmentMenu] Please log in to upload files');
      return;
    }

    // Validate all files
    const validFiles = [];
    for (const file of files) {
      const validation = validateFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        console.error(`Invalid file ${file.name}:`, validation.error);
      }
    }

    if (validFiles.length === 0) {
      console.error('No valid files to upload');
      return;
    }

    // Create immediate previews for images (so chips appear instantly)
    // Do this before try/catch so it's available in error handler
    let filePreviews = [];
    try {
      filePreviews = await Promise.all(
        validFiles.map(file => {
          if (file.type.startsWith('image/')) {
            return new Promise((resolve) => {
              const reader = new FileReader();
              reader.onload = (e) => resolve({ file, preview: e.target.result });
              reader.onerror = () => resolve({ file, preview: null });
              reader.readAsDataURL(file);
            });
          }
          return Promise.resolve({ file, preview: null });
        })
      );
    } catch (previewError) {
      console.warn('Error creating previews:', previewError);
      // Create empty previews array as fallback
      filePreviews = validFiles.map(file => ({ file, preview: null }));
    }

    try {
      // Get default collection ("All")
      await topicService.ensureAllCollection(user.id);
      const { data: collections } = await topicService.getUserTopics(user.id);
      const allCollection = collections?.find(c => c.name === 'All');
      
      if (!allCollection) {
        console.error('Could not find default collection');
        // Still create chips even if collection is missing
        for (let i = 0; i < validFiles.length; i++) {
          const file = validFiles[i];
          const filePreview = filePreviews[i] || { file, preview: null };
          const tempChip = {
            id: crypto.randomUUID(),
            type: 'item',
            label: file.name,
            thumbnail: filePreview.preview || null,
            file_url: null,
            file_name: file.name,
            file_type: file.type,
            file_size: file.size,
            source: 'upload',
          };
          addChip(tempChip);
        }
        return;
      }

      console.log('📤 Starting file upload...', { fileCount: validFiles.length, files: validFiles.map(f => ({ name: f.name, type: f.type, size: f.size })) });

      // Upload all files using upload service
      const result = await uploadFiles(validFiles, '', allCollection.id);

      console.log('📥 Upload result:', result);

      // Handle different response structures
      let uploadedItems = [];
      if (result?.items && result.items.length > 0) {
        uploadedItems = result.items;
      } else if (result?.data?.items && result.data.items.length > 0) {
        uploadedItems = result.data.items;
      } else if (result?.id) {
        // If result itself is a single item
        uploadedItems = [result];
      }

      // Process each uploaded file and create chips
      // Import supabase once outside the loop
      let supabase;
      try {
        const supabaseModule = await import('../../lib/supabase');
        supabase = supabaseModule.supabase;
      } catch (importError) {
        console.error('Failed to import supabase:', importError);
        supabase = null;
      }
      
      // Process each file individually with error handling to prevent one failure from crashing all
      for (let i = 0; i < validFiles.length; i++) {
        try {
          // DEFENSIVE: Check if file exists
          const file = validFiles[i];
          if (!file || typeof file !== 'object') {
            console.error(`❌ Invalid file at index ${i}:`, file);
            continue; // Skip invalid files
          }

          // DEFENSIVE: Ensure file has required properties
          if (!file.name || typeof file.name !== 'string') {
            console.error(`❌ File at index ${i} missing name property:`, file);
            continue;
          }

          // Use index-based matching since file object references might not match
          const filePreview = filePreviews[i] || { file, preview: null };
          const preview = filePreview?.preview || null;
          const uploadedItem = uploadedItems[i] || null;

          if (!uploadedItem) {
            console.error(`❌ Upload completed but no item returned for file ${file.name}`);
            // Still create a chip with the file info so user sees something
            const tempChip = {
              id: crypto.randomUUID(),
              type: 'item',
              label: file.name || 'Unknown file',
              thumbnail: preview || null,
              file_url: null,
              file_name: file.name || 'Unknown file',
              file_type: file.type || 'unknown',
              file_size: file.size || null,
              source: 'upload',
            };
            addChip(tempChip);
            console.log('✅ Added temporary chip from file (upload may still be processing):', tempChip);
            continue;
          }

          // DEFENSIVE: Validate uploadedItem structure
          if (typeof uploadedItem !== 'object' || uploadedItem === null) {
            console.error(`❌ Invalid uploadedItem for file ${file.name}:`, uploadedItem);
            // Create fallback chip
            const fallbackChip = {
              id: crypto.randomUUID(),
              type: 'item',
              label: file.name || 'Unknown file',
              thumbnail: preview || null,
              file_url: null,
              file_name: file.name || 'Unknown file',
              file_type: file.type || 'unknown',
              file_size: file.size || null,
              source: 'upload',
            };
            addChip(fallbackChip);
            continue;
          }

          console.log('📦 Uploaded item:', uploadedItem);
          
          // DEFENSIVE: Get file URL with null checks
          let fileUrl = null;
          if (uploadedItem && typeof uploadedItem === 'object') {
            fileUrl = uploadedItem.url || uploadedItem.file_url || null;
          }
          
          // If fileUrl is not a full URL, try to get it from storage
          if (fileUrl && typeof fileUrl === 'string' && !fileUrl.startsWith('http') && !fileUrl.startsWith('data:') && supabase) {
            try {
              if (fileUrl.includes('/') || fileUrl.startsWith('uploads/')) {
                const path = fileUrl.startsWith('uploads/') ? fileUrl.replace('uploads/', '') : fileUrl;
                const { data } = supabase.storage.from('user_uploads').getPublicUrl(path);
                if (data && data.publicUrl) {
                  fileUrl = data.publicUrl;
                }
              }
            } catch (error) {
              console.warn('Failed to convert file URL to public URL:', error);
              fileUrl = null;
            }
          }
          
          // DEFENSIVE: Get thumbnail URL with null checks
          let thumbnailUrl = preview || null;
          if (uploadedItem && typeof uploadedItem === 'object') {
            thumbnailUrl = preview || uploadedItem.thumbnail_url || null;
          }
          
          if (thumbnailUrl && typeof thumbnailUrl === 'string' && !thumbnailUrl.startsWith('http') && !thumbnailUrl.startsWith('data:') && !thumbnailUrl.startsWith('linear-gradient') && supabase) {
            try {
              if (thumbnailUrl.includes('/') || thumbnailUrl.startsWith('uploads/')) {
                const path = thumbnailUrl.startsWith('uploads/') ? thumbnailUrl.replace('uploads/', '') : thumbnailUrl;
                const { data } = supabase.storage.from('user_uploads').getPublicUrl(path);
                if (data && data.publicUrl) {
                  thumbnailUrl = data.publicUrl;
                }
              }
            } catch (error) {
              console.warn('Failed to convert thumbnail to public URL:', error);
              thumbnailUrl = preview || null; // Fallback to preview
            }
          }
          
          // DEFENSIVE: Create chip with all necessary fields and fallbacks
          const chip = {
            id: (uploadedItem && typeof uploadedItem === 'object' && uploadedItem.id) ? String(uploadedItem.id) : crypto.randomUUID(),
            type: 'item',
            label: (uploadedItem && typeof uploadedItem === 'object' && uploadedItem.title) ? String(uploadedItem.title) : (file.name || 'Unknown file'),
            thumbnail: thumbnailUrl || fileUrl || null, // Use file_url as fallback for thumbnail
            // Store full file information for attachments
            file_url: fileUrl || null,
            file_name: (uploadedItem && typeof uploadedItem === 'object' && uploadedItem.title) ? String(uploadedItem.title) : (file.name || 'Unknown file'),
            file_type: file.type || (uploadedItem && typeof uploadedItem === 'object' && uploadedItem.meta && typeof uploadedItem.meta === 'object' && uploadedItem.meta.filetype) ? uploadedItem.meta.filetype : 'unknown',
            file_size: file.size || (uploadedItem && typeof uploadedItem === 'object' && uploadedItem.meta && typeof uploadedItem.meta === 'object' && uploadedItem.meta.filesize) ? uploadedItem.meta.filesize : null,
            source: 'upload', // Track that this came from file upload
          };

          console.log('🎴 Creating chip:', chip);
          addChip(chip);
        } catch (fileError) {
          // Handle individual file processing errors without crashing the whole upload
          console.error(`❌ Error processing file at index ${i}:`, fileError);
          
          // DEFENSIVE: Safely get file info with fallbacks
          const file = validFiles[i];
          const filePreview = filePreviews[i] || (file ? { file, preview: null } : null);
          
          // Only create chip if we have valid file info
          if (file && typeof file === 'object' && file.name) {
            const errorChip = {
              id: crypto.randomUUID(),
              type: 'item',
              label: file.name || 'Unknown file',
              thumbnail: (filePreview && filePreview.preview) || null,
              file_url: null,
              file_name: file.name || 'Unknown file',
              file_type: file.type || 'unknown',
              file_size: file.size || null,
              source: 'upload',
            };
            addChip(errorChip);
            console.log('✅ Added chip despite processing error:', errorChip);
          } else {
            console.error(`❌ Cannot create chip for invalid file at index ${i}`);
          }
        }
      }
      
      console.log('✅ All chips processed. Files processed:', validFiles.length);
    } catch (error) {
      console.error('❌ Upload error:', error);
      console.error('Error details:', error.message, error.stack);
      // Even on error, create chips with file info so user sees something
      for (let i = 0; i < validFiles.length; i++) {
        try {
          // DEFENSIVE: Validate file before creating chip
          const file = validFiles[i];
          if (!file || typeof file !== 'object' || !file.name) {
            console.error(`❌ Skipping invalid file at index ${i} in error handler`);
            continue;
          }
          
          const filePreview = filePreviews[i] || { file, preview: null };
          const errorChip = {
            id: crypto.randomUUID(),
            type: 'item',
            label: file.name || 'Unknown file',
            thumbnail: (filePreview && filePreview.preview) || null,
            file_url: null,
            file_name: file.name || 'Unknown file',
            file_type: file.type || 'unknown',
            file_size: file.size || null,
            source: 'upload', // Track that this came from file upload
          };
          addChip(errorChip);
          console.log('✅ Added chip despite upload error:', errorChip);
        } catch (chipError) {
          // Even error handling can fail - log and continue
          console.error(`❌ Failed to create error chip for file at index ${i}:`, chipError);
        }
      }
    } finally {
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUploadClick = () => {
    console.log('🔵 [AttachmentMenu] handleUploadClick called', {
      hasFileInput: !!fileInputRef.current,
    });
    fileInputRef.current?.click();
    console.log('🔵 [AttachmentMenu] File input clicked');
  };

  return (
    <div className="relative">
      <input
        ref={fileInputRef}
        type="file"
        accept="*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />
      <button
        onClick={handleUploadClick}
        className="flex-shrink-0 w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm border border-neutral-200/60 flex items-center justify-center text-neutral-600 hover:bg-white hover:text-purple-600 transition-all duration-200 shadow-sm hover:shadow-md relative"
        title="Upload file"
      >
        <Plus size={18} />
        {/* Count only chips from uploads */}
        {(() => {
          const uploadCount = chips.filter(c => c.source === 'upload').length;
          return uploadCount > 0 ? (
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-purple-600 text-white text-[10px] flex items-center justify-center font-bold">
              {uploadCount > 9 ? '9+' : uploadCount}
            </span>
          ) : null;
        })()}
      </button>
    </div>
  );
}
